import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { Badge, EmptyState, StatCard } from "@/components/DashUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { rupee, formatApiErrorDetail } from "@/lib/api";
import { FolderKanban, Check, Clock, Link2, FileEdit, Loader2 } from "lucide-react";

export default function ClientPortal() {
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rev, setRev] = useState({ title: "", details: "" });
  const [revOpen, setRevOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/client/projects");
    setProjects(data);
    if (data.length) openProject(data[0].id);
    setLoading(false);
  };
  const openProject = async (id) => { const { data } = await api.get(`/client/projects/${id}`); setActive(data); };
  useEffect(() => { load(); }, []);

  const submitRevision = async () => {
    try {
      await api.post(`/client/projects/${active.id}/revisions`, rev);
      toast.success("Revision request submitted.");
      setRev({ title: "", details: "" }); setRevOpen(false);
      openProject(active.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell title="Client Portal">
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-violet-400 animate-spin" /></div> :
        projects.length === 0 ? <EmptyState icon={FolderKanban} title="No projects yet" hint="Your project will appear here once we begin." /> :
        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 space-y-2">
            {projects.map((p) => (
              <button key={p.id} data-testid={`client-project-${p.id}`} onClick={() => openProject(p.id)}
                className={`w-full text-left glass rounded-xl p-4 card-hover ${active?.id === p.id ? "ring-1 ring-violet-500/50" : ""}`}>
                <div className="font-semibold text-white text-sm">{p.name}</div>
                <div className="mt-2"><Badge status={p.stage} /></div>
              </button>
            ))}
          </div>

          {active && (
            <div className="lg:col-span-3 space-y-5" data-testid="client-project-detail">
              <div className="glass-strong rounded-2xl p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="font-display text-2xl font-bold text-white">{active.name}</h2>
                    <div className="mt-2"><Badge status={active.stage} /></div>
                  </div>
                  <Dialog open={revOpen} onOpenChange={setRevOpen}>
                    <DialogTrigger asChild><Button data-testid="client-request-revision" className="bg-violet-600 hover:bg-violet-500 text-white"><FileEdit className="w-4 h-4 mr-1.5" />Request Revision</Button></DialogTrigger>
                    <DialogContent className="glass-strong border-white/10 text-white">
                      <DialogHeader><DialogTitle>Request a revision</DialogTitle></DialogHeader>
                      <Input placeholder="Title" className="bg-white/5 border-white/10 text-white" value={rev.title} onChange={(e) => setRev({ ...rev, title: e.target.value })} data-testid="revision-title" />
                      <Textarea placeholder="Describe the change…" className="bg-white/5 border-white/10 text-white" value={rev.details} onChange={(e) => setRev({ ...rev, details: e.target.value })} data-testid="revision-details" />
                      <Button className="bg-violet-600 hover:bg-violet-500 text-white" onClick={submitRevision} disabled={!rev.title} data-testid="revision-submit">Submit</Button>
                    </DialogContent>
                  </Dialog>
                </div>
                <p className="text-slate-400 text-sm mt-4 leading-relaxed">{active.scope}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div className="glass rounded-2xl p-6">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-cyan-400" />Milestones</h3>
                  <ul className="space-y-3">
                    {(active.milestones || []).map((m, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm">
                        <span className={`grid place-items-center w-5 h-5 rounded-full ${m.done ? "bg-emerald-500 text-white" : "bg-white/10 text-slate-500"}`}>{m.done && <Check className="w-3 h-3" />}</span>
                        <span className={m.done ? "text-slate-400 line-through" : "text-slate-200"}>{m.title}</span>
                        {m.target_date && <span className="ml-auto text-xs text-slate-500 font-mono">{m.target_date}</span>}
                      </li>
                    ))}
                    {!active.milestones?.length && <li className="text-sm text-slate-500">Milestones will appear here.</li>}
                  </ul>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="font-semibold text-white mb-4">Payment records</h3>
                  {active.payments?.length ? (
                    <ul className="space-y-2.5">
                      {active.payments.map((p) => (
                        <li key={p.id} className="flex items-center justify-between text-sm">
                          <span className="text-slate-300">{p.note || p.method || "Payment"} {p.verified && <Check className="inline w-3.5 h-3.5 text-emerald-400 ml-1" />}</span>
                          <span className="font-mono text-white">{rupee(p.amount_rupees)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="text-sm text-slate-500">No payments recorded yet.</p>}
                  <p className="text-xs text-slate-500 mt-4">Payments are recorded and verified by Nova admin.</p>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="font-semibold text-white mb-4">Requested information</h3>
                  <ul className="space-y-2">{(active.requested_info || []).map((r, i) => (<li key={i} className="text-sm text-slate-300">• {r}</li>))}
                    {!active.requested_info?.length && <li className="text-sm text-slate-500">Nothing requested right now.</li>}</ul>
                </div>

                <div className="glass rounded-2xl p-6">
                  <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><Link2 className="w-4 h-4 text-violet-400" />Preview links</h3>
                  <ul className="space-y-2">{(active.preview_links || []).map((l, i) => (<li key={i}><a href={l.url} className="text-sm text-cyan-300 hover:underline">{l.label}</a></li>))}
                    {!active.preview_links?.length && <li className="text-sm text-slate-500">Preview links will appear here.</li>}</ul>
                </div>
              </div>

              <div className="glass rounded-2xl p-6">
                <h3 className="font-semibold text-white mb-4">Handover checklist</h3>
                <ul className="space-y-3 sm:grid sm:grid-cols-3 sm:gap-3 sm:space-y-0">
                  {(active.handover_checklist || []).map((h, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-sm">
                      <span className={`grid place-items-center w-5 h-5 rounded-full ${h.done ? "bg-emerald-500 text-white" : "bg-white/10 text-slate-500"}`}>{h.done && <Check className="w-3 h-3" />}</span>
                      <span className={h.done ? "text-slate-400" : "text-slate-200"}>{h.item}</span>
                    </li>
                  ))}
                  {!active.handover_checklist?.length && <li className="text-sm text-slate-500">Handover items appear here near launch.</li>}
                </ul>
              </div>

              {active.revisions?.length > 0 && (
                <div className="glass rounded-2xl p-6">
                  <h3 className="font-semibold text-white mb-4">Your revision requests</h3>
                  <ul className="space-y-2">{active.revisions.map((r) => (<li key={r.id} className="flex justify-between text-sm"><span className="text-slate-300">{r.title}</span><Badge status={r.status} /></li>))}</ul>
                </div>
              )}
            </div>
          )}
        </div>}
    </DashboardShell>
  );
}
